// Types for the onboarding workflow

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  component: React.ComponentType<{
    onNext: (data: Record<string, any>) => void;
    data?: Record<string, any>;
    isLastStep?: boolean;
  }>;
}

/**
 * Estados possíveis do processo de onboarding
 */
export enum OnboardingStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED'
}

/**
 * Estado do processo de onboarding
 */
export interface OnboardingState {
  user_id: string;
  status: OnboardingStatus;
  current_step: number;
  steps_data: Record<string, any>;
  last_updated: string;
  completed_at?: string;
}

/**
 * Dados de integração com WhatsApp
 */
export interface WhatsappIntegrationData {
  phone_number: string;
  instance_name: string;
  is_connected?: boolean;
  qr_code_scanned?: boolean;
  qr_code?: string;
  webhook_url?: string;
  credential_id?: string;
  credential_name?: string;
  credential_created?: boolean;
}

// Business info step
export interface BusinessData {
  name?: string;
  segment?: string;
  description?: string;
  address?: string;
  website?: string;
  logo_url?: string;
}

// AI Agent step
export interface AIAgentData {
  name?: string;
  personality?: 'friendly' | 'professional' | 'helpful' | 'casual' | 'formal' | 'custom';
  custom_personality?: string;
  example_phrases?: string[];
}

// Calendar step
export interface DayAvailability {
  day: string; // monday, tuesday, etc.
  available: boolean;
  slots?: Array<{ start: string; end: string }>;
}

export interface CalendarData {
  availability?: DayAvailability[];
  appointment_duration?: number; // in minutes
  buffer_time?: number; // in minutes
  max_daily_appointments?: number;
  auto_confirm?: boolean;
  reminder_time?: number[]; // minutes before appointment (e.g. [60, 1440] for 1 hour and 1 day)
}

// Content step
export interface Service {
  name: string;
  description: string;
  price?: number;
  image_url?: string;
}

export interface Product {
  name: string;
  description: string;
  price: number;
  image_url?: string;
  category?: string;
  variations?: string[];
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface ContentData {
  services?: Service[];
  products?: Product[];
  faq?: FAQItem[];
}

// Complete onboarding data
export interface OnboardingData {
  whatsapp?: WhatsappIntegrationData;
  business?: BusinessData;
  ai_agent?: AIAgentData;
  calendar?: CalendarData;
  content?: ContentData;
}

// Event emitted during onboarding progress
export interface OnboardingProgressEvent {
  step: string;
  status: 'started' | 'completed' | 'failed';
  data?: Record<string, any>;
  error?: string;
}

// Interface for the n8n template types we'll support
export interface N8nTemplateType {
  id: string;
  name: string;
  description: string;
  category: 'whatsapp' | 'calendar' | 'ai' | 'ecommerce' | 'generic';
  icon: string;
} 